import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ArrowRight, Users, Home as HomeIcon, Calendar, DollarSign, BookOpen, Sparkles, TrendingUp } from 'lucide-react';

const Home: React.FC = () => {
  // Stub data for featured pets
  const featuredPets = [
    { id: 1, name: 'Bibble', age: '2 years', trait: 'Gentle soul', image: 'https://adopets-prod.s3.amazonaws.com/organization/pet/picture/AUTOx400/2025731_181531_1756664131574.jpeg' },
    { id: 2, name: 'Lena', age: '3 years', trait: 'Playful companion', image: 'https://adopets-prod.s3.amazonaws.com/organization/pet/picture/AUTOx400/2025730_16258_1756569778976.jpeg' },
    { id: 3, name: 'Toothbrush', age: '1 year', trait: 'Cuddle expert', image: 'https://adopets-prod.s3.amazonaws.com/organization/pet/picture/AUTOx400/2025730_17549_1756573549249.jpeg' },
  ];

  const impactStats = [
    { label: 'Pets Adopted This Year', value: 847 },
    { label: 'Volunteer Hours', value: 12500 },
    { label: 'Lives Saved', value: 1200 },
    { label: 'Happy Families', value: 750 },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-deep-forest/80 to-deep-forest/40 z-10" />
        <img
          src="/ivy-lilah.jpg"
          alt="Happy pets"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display text-5xl md:text-7xl text-white mb-6"
          >
            Your New Best Friend is Waiting
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-white/90 mb-8"
          >
            Adopting a shelter pet not only saves a life, it enriches your own.
            Join us in creating a compassionate journey for pets and people.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/adopt"
              className="bg-warm-coral hover:bg-warm-coral/90 text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 transition-all transform hover:scale-105"
            >
              Meet Our Pets
              <Heart size={20} fill="currentColor" />
            </Link>
            <Link
              to="/about"
              className="bg-sky-blue hover:bg-sky-blue/90 text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 transition-all transform hover:scale-105"
            >
              Start Your Journey
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Quick Actions Bar */}
      <section className="bg-white shadow-lg sticky top-20 z-40 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/adopt"
              className="flex items-center justify-center gap-3 p-3 rounded-xl hover:bg-cream-white transition-colors"
            >
              <div className="bg-warm-coral/10 p-2 rounded-lg">
                <Heart className="text-warm-coral" size={24} />
              </div>
              <span className="font-semibold text-deep-forest">Adopt a Pet</span>
            </Link>
            <Link
              to="/donate"
              className="flex items-center justify-center gap-3 p-3 rounded-xl hover:bg-cream-white transition-colors"
            >
              <div className="bg-sky-blue/10 p-2 rounded-lg">
                <DollarSign className="text-sky-blue" size={24} />
              </div>
              <span className="font-semibold text-deep-forest">Donate Now</span>
            </Link>
            <Link
              to="/volunteer"
              className="flex items-center justify-center gap-3 p-3 rounded-xl hover:bg-cream-white transition-colors"
            >
              <div className="bg-sage-green/20 p-2 rounded-lg">
                <Users className="text-sage-green" size={24} />
              </div>
              <span className="font-semibold text-deep-forest">Volunteer</span>
            </Link>
            <Link
              to="/services/lost-found"
              className="flex items-center justify-center gap-3 p-3 rounded-xl hover:bg-cream-white transition-colors"
            >
              <div className="bg-golden-yellow/20 p-2 rounded-lg">
                <HomeIcon className="text-golden-yellow" size={24} />
              </div>
              <span className="font-semibold text-deep-forest">Lost Pet?</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Pets Carousel */}
      <section className="py-16 bg-cream-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl text-deep-forest mb-4">Love at First Sight</h2>
            <p className="text-warm-gray text-lg">Meet some of our wonderful pets looking for their forever homes</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {featuredPets.map((pet, index) => (
              <motion.div
                key={pet.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="relative">
                  <img
                    src={pet.image}
                    alt={pet.name}
                    className="w-full h-80 object-cover"
                  />
                  <button className="absolute top-4 right-4 bg-white/90 p-2 rounded-full hover:bg-warm-coral hover:text-white transition-colors">
                    <Heart size={24} />
                  </button>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl text-deep-forest mb-2">{pet.name}</h3>
                  <p className="text-warm-gray mb-1">{pet.age}</p>
                  <p className="text-sky-blue font-semibold mb-4">{pet.trait}</p>
                  <Link
                    to={`/adopt/pet/${pet.id}`}
                    className="block w-full bg-warm-coral hover:bg-warm-coral/90 text-white text-center py-3 rounded-full font-semibold transition-colors"
                  >
                    Meet Me
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/adopt"
              className="inline-flex items-center gap-2 text-warm-coral hover:text-warm-coral/80 font-semibold text-lg transition-colors"
            >
              View All Available Pets
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-gradient-to-r from-sky-blue to-sage-green">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl text-white mb-4">Our Impact</h2>
            <p className="text-white/90 text-lg">Together, we're making a difference in our community</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl font-bold text-white mb-2">
                  {stat.value.toLocaleString()}+
                </div>
                <div className="text-white/80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl text-deep-forest mb-4">How We Help</h2>
            <p className="text-warm-gray text-lg">Comprehensive programs for pets and people in our community</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link
              to="/services/medical"
              className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="bg-warm-coral/10 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-warm-coral/20 transition-colors">
                <Heart className="text-warm-coral" size={32} />
              </div>
              <h3 className="font-semibold text-xl text-deep-forest mb-2">Medical Services</h3>
              <p className="text-warm-gray">Lovebug's Legacy fund helps pets with special medical needs</p>
            </Link>
            <Link
              to="/foster"
              className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="bg-sky-blue/10 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-sky-blue/20 transition-colors">
                <HomeIcon className="text-sky-blue" size={32} />
              </div>
              <h3 className="font-semibold text-xl text-deep-forest mb-2">Foster Program</h3>
              <p className="text-warm-gray">Provide temporary homes for pets awaiting adoption</p>
            </Link>
            <Link
              to="/programs/youth"
              className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="bg-golden-yellow/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-golden-yellow/30 transition-colors">
                <Sparkles className="text-golden-yellow" size={32} />
              </div>
              <h3 className="font-semibold text-xl text-deep-forest mb-2">Youth Programs</h3>
              <p className="text-warm-gray">Summer camps, birthday parties, and Youth Allies program</p>
            </Link>
            <Link
              to="/programs/education"
              className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="bg-soft-lavender/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-soft-lavender/30 transition-colors">
                <BookOpen className="text-soft-lavender" size={32} />
              </div>
              <h3 className="font-semibold text-xl text-deep-forest mb-2">Humane Education</h3>
              <p className="text-warm-gray">Free educational programs for classrooms and organizations</p>
            </Link>
            <Link
              to="/events"
              className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="bg-sage-green/20 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-sage-green/30 transition-colors">
                <Calendar className="text-sage-green" size={32} />
              </div>
              <h3 className="font-semibold text-xl text-deep-forest mb-2">Community Events</h3>
              <p className="text-warm-gray">Walk for Animals, Fur Ball Gala, and more fun events</p>
            </Link>
            <Link
              to="/volunteer"
              className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="bg-warm-coral/10 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-warm-coral/20 transition-colors">
                <Users className="text-warm-coral" size={32} />
              </div>
              <h3 className="font-semibold text-xl text-deep-forest mb-2">Volunteer</h3>
              <p className="text-warm-gray">Join our team of dedicated volunteers making a difference</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Success Story */}
      <section className="py-16 bg-cream-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-4xl text-deep-forest mb-6">Happy Tails</h2>
              <h3 className="text-2xl font-semibold text-warm-coral mb-4">Popo's Journey</h3>
              <p className="text-warm-gray mb-4 leading-relaxed">
                In April 2015, Popo arrived at Animal Allies as a 5-year-old Staffordshire terrier mix who was so overweight 
                that she couldn't walk. Not only because of her severe obesity, but also because she had torn both rear CCLs.
              </p>
              <p className="text-warm-gray mb-6 leading-relaxed">
                After losing 40 lbs over a year's time and receiving the medical care she needed through our special medical fund, 
                Popo went from being unable to walk without assistance to running around and rolling in the grass without the burden 
                of injuries or excessive weight.
              </p>
              <Link
                to="/about/stories"
                className="inline-flex items-center gap-2 bg-warm-coral hover:bg-warm-coral/90 text-white px-6 py-3 rounded-full font-semibold transition-colors"
              >
                Read More Stories
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="/popo.png"
                alt="Happy adopted dog"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-sky-blue text-white p-4 rounded-2xl shadow-lg">
                <TrendingUp size={24} className="mb-2" />
                <p className="font-bold">1,200+ Lives Saved</p>
                <p className="text-sm">This Year Alone</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-warm-coral to-sky-blue">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-white mb-6">
            Every Pet Deserves a Loving Home
          </h2>
          <p className="text-white/90 text-xl mb-8">
            Whether you adopt, donate, volunteer, or foster, you're saving lives and making a difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/adopt"
              className="bg-white hover:bg-cream-white text-warm-coral px-8 py-4 rounded-full font-semibold text-lg transition-colors"
            >
              Adopt Today
            </Link>
            <Link
              to="/donate"
              className="bg-deep-forest hover:bg-deep-forest/90 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors"
            >
              Support Our Mission
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
